import { useState, useRef, type MouseEvent } from "react";
import classnames from "classnames";
import { Image, Crop } from "react-feather";

import style from "./style.module.scss";
import UploadImage from "../upload-image";
import PhotoLibraryPicker from "../photo-library-picker";
import ImageSizeIndicator from "../image-size-indicator";
import ImageEditor from "../image-editor";
import { useUser } from "../../../data/context/auth-context";
import { DropdownMenu, DropdownMenuItem } from "../../../ui";
import {
  normalizePhoto,
  createPhotoWithTransform,
  getBackgroundTransformStyles,
  type PhotoTransform,
} from "../../utils/photo-transform";
import type { PhotoValue } from "../../../product/types";

interface ImageContainerProps {
  productId?: string;
  userId?: string;
  onChange?: (photo: PhotoValue) => void;
  image?: PhotoValue | null;
  className?: string;
  imageClassName?: string;
  isPreview?: boolean;
  aspectRatio?: number;
  cropShape?: "rect" | "round";
}

function ImageContainer({
  productId,
  userId,
  onChange,
  image,
  className,
  imageClassName,
  isPreview,
  aspectRatio = 1,
  cropShape = "rect",
}: ImageContainerProps) {
  const user = useUser();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  const targetUserId = userId || user?.uid;
  const { url, transform } = normalizePhoto(image);
  const canUpload = productId && targetUserId;

  const transformStyles = getBackgroundTransformStyles(transform);
  const imageStyle = url ? { backgroundImage: `url("${url}")`, ...transformStyles } : {};

  const handleImageChange = (newUrl: string) => {
    onChange?.(newUrl);
  };

  const handleTransformSave = (newTransform: PhotoTransform) => {
    if (url) {
      onChange?.(createPhotoWithTransform(url, newTransform));
    }
  };

  const handleImageClick = (e: MouseEvent<HTMLDivElement>) => {
    if (isPreview) return;

    // Don't open menu if clicking on upload area
    const target = e.target as HTMLElement;
    if (target.closest(`.${style.uploadWrapper}`)) {
      return;
    }

    if (url) {
      setIsMenuOpen(true);
    }
  };

  const handleReplaceClick = () => {
    setIsMenuOpen(false);
    setIsPickerOpen(true);
  };

  const handleEditClick = () => {
    setIsMenuOpen(false);
    setIsEditorOpen(true);
  };

  return (
    <div className={classnames(className, style.container)}>
      {!isPreview && <ImageSizeIndicator className={style.indicator} src={url} />}
      <div
        ref={imageRef}
        className={classnames(style.image, imageClassName, {
          [style.isEmpty]: !url,
        })}
        style={imageStyle}
        onClick={handleImageClick}
      >
        {/* Show upload UI when no image and upload is available */}
        {!isPreview && !url && canUpload && (
          <div className={style.uploadWrapper}>
            <UploadImage
              onChange={handleImageChange}
              productId={productId}
              userId={targetUserId}
              className={style.upload}
            />
          </div>
        )}
      </div>

      <DropdownMenu triggerRef={imageRef} open={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
        <DropdownMenuItem icon={<Image size={16} />} onClick={handleReplaceClick}>
          Remplacer l'image
        </DropdownMenuItem>
        <DropdownMenuItem icon={<Crop size={16} />} onClick={handleEditClick}>
          Recadrer / zoomer
        </DropdownMenuItem>
      </DropdownMenu>

      {canUpload && (
        <PhotoLibraryPicker
          open={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          onSelect={handleImageChange}
          productId={productId}
          userId={targetUserId}
        />
      )}

      {url && !isPreview && (
        <ImageEditor
          open={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          imageUrl={url}
          initialTransform={transform}
          aspectRatio={aspectRatio}
          cropShape={cropShape}
          onSave={handleTransformSave}
        />
      )}
    </div>
  );
}

export default ImageContainer;
